import { useFormState, useWatch } from "react-hook-form";
import useProjectsLinked, {
    useClientsFilterApi,
    useProjectRelationTypes,
    useProjectsFilterApi,
} from "features/admin/projects/single/useProjectsLinked";

import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import Form from "shared/components/inputs/form/Form";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import PropTypes from "prop-types";
import VStack from "shared/components/layout/stack/VStack";
import { getDropdownOptions } from "features/admin/offices/OfficesForm";
import { getIdOfLinkedProjectRelationType } from "features/admin/projects/single/ProjectsLinked";
import { sendNotification } from "shared/components/feedback/notification/notification";
import { useParams } from "react-router";
import { useProjectsApi } from "features/admin/projects/useProjects";
import { useState } from "react";

const ModalCTA = ({ isEditing, isLoading, onClose }) => {
    const { isValid } = useFormState();

    return (
        <ModalActions
            primaryAction={{
                type: "submit",
                label: isEditing ? "Save" : "Link Project",
                disabled: !isValid,
            }}
            secondaryAction={{
                type: "button",
                label: "Cancel",
                onClick: onClose,
            }}
            {...(isEditing && {
                tertiaryAction: {
                    type: "button",
                    leadingIcon: "delete",
                    label: "Delete",
                    onClick: () => void 0,
                },
            })}
            as="fieldset"
            disabled={isLoading}
        />
    );
};

function transformProjectLinkDataToFormData(
    linkToProjectDefaultVal,
    projectLinkData = {}
) {
    return {
        linkTo: linkToProjectDefaultVal.value,
        client: "",
        project: projectLinkData.projectRelatedProjectID ?? "",
    };
}

function transformFormDataToProjectLinkData(projectLinkData = {}) {
    const newProjectLinkData = { ...projectLinkData };

    return newProjectLinkData;
}

let projectssDropdownOptions = [];
const RenderProject = ({ disabledOptionsList }) => {
    const clientId = useWatch({ name: "client" });

    const { useProjectsFilter } = useProjectsFilterApi();
    const projectsFilter = useProjectsFilter(clientId);

    projectssDropdownOptions =
        !projectsFilter?.isLoading && projectsFilter?.data
            ? getDropdownOptions(projectsFilter?.data, "JobId", "JobName")
            : [];
    return (
        <Dropdown
            name="project"
            validations={{
                required: true,
            }}
            label="Project"
            options={projectssDropdownOptions}
            disabledOptionsList={disabledOptionsList}
        />
    );
};

const ProjectLinkForm = ({ projectLinkIdToEdit, onClose }) => {
    const { projectId } = useParams();
    const { useProjectById } = useProjectsApi();
    const projectData = useProjectById(projectId);
    const { useProjectsRelatedList, createRelatedProject } =
        useProjectsLinked();
    const relatedProjectsList = useProjectsRelatedList({ jobID: projectId });
    const { clientsFilter } = useClientsFilterApi();
    const { projectRelationTypes } = useProjectRelationTypes();

    const [inProgress, setInProgress] = useState(false);

    if (relatedProjectsList?.isLoading || projectData.isLoading) {
        return <>Loading...</>;
    }

    if (relatedProjectsList?.isError || projectData.isError) {
        return <>Error</>;
    }

    const isEditing = projectLinkIdToEdit !== undefined;
    const modifier = createRelatedProject;

    const currentProjectLinkData = relatedProjectsList.data.find(
        (projectLink) => projectLink.relatedJobID === projectLinkIdToEdit
    );

    if (isEditing && !currentProjectLinkData) {
        return <div>Could not find project link</div>;
    }

    const clientsDropdownOptions =
        !clientsFilter?.isLoading && clientsFilter?.data
            ? getDropdownOptions(clientsFilter?.data, "ClientId")
            : [];

    const linkToOptions = [
        { value: projectData?.data?.JobId, label: projectData?.data?.Name },
    ];

    const onSubmit = async (formData) => {
        const typeId = getIdOfLinkedProjectRelationType(
            projectRelationTypes?.data
        );
        const data = transformFormDataToProjectLinkData(currentProjectLinkData);

        const obj = {
            projectId: projectId,
            projectRelatedProjectID: formData.project,
            relationTypeId: typeId,
            body: data,
        };

        const linkId = isEditing
            ? currentProjectLinkData.projectRelatedProjectID
            : formData.project;

        const linkName =
            projectssDropdownOptions.length > 0
                ? projectssDropdownOptions.find((f) => f.value === linkId)
                      ?.label
                : "";

        await modifier.mutateAsync(obj);
        setInProgress(false);
        sendNotification(
            isEditing ? (
                <>
                    <strong>{linkName}</strong> link has been updated
                </>
            ) : (
                <>
                    <strong>{linkName}</strong> has been linked to{" "}
                    <strong>{projectData?.data?.Name}</strong>
                </>
            )
        );
        onClose();
    };

    return (
        <Form
            onSubmit={onSubmit}
            defaultValues={transformProjectLinkDataToFormData(
                linkToOptions[0],
                currentProjectLinkData
            )}
        >
            <VStack spacing="Three">
                <VStack spacing="Two">
                    <Dropdown
                        name="linkTo"
                        validations={{
                            required: true,
                        }}
                        label="Link To"
                        options={linkToOptions}
                        disabled={true}
                    />
                    <Dropdown
                        name="client"
                        validations={{
                            required: true,
                        }}
                        label="Client"
                        itemsAreLoading={clientsFilter?.isLoading}
                        options={clientsDropdownOptions}
                    />
                    <RenderProject
                        disabledOptionsList={
                            relatedProjectsList.isLoading
                                ? []
                                : relatedProjectsList.data.map((data) => ({
                                      value: data.relatedJobID,
                                  }))
                        }
                    />
                </VStack>

                <ModalCTA
                    isEditing={isEditing}
                    onClose={onClose}
                    isLoading={modifier.isLoading || inProgress}
                />
            </VStack>
        </Form>
    );
};

ModalCTA.propTypes = {
    isEditing: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

RenderProject.propTypes = {
    disabledOptionsList: PropTypes.array
};

ProjectLinkForm.propTypes = {
    projectLinkIdToEdit: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default ProjectLinkForm;
